import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronLeft, Target } from 'lucide-react';

export interface WalkthroughStep {
  id: string;
  targetSelector: string; // CSS selector for the element to highlight
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  waitForClick?: boolean; // If true, waits for user to click the target element
  onBeforeStep?: () => void | Promise<void>; // Called before showing this step
  onAfterStep?: () => void | Promise<void>; // Called after completing this step
  skipIfNotFound?: boolean; // Skip step if target element not found
}

export interface WalkthroughConfig {
  id: string;
  name: string;
  steps: WalkthroughStep[];
  onComplete?: () => void;
  onSkip?: () => void;
}

interface WalkthroughProps {
  config: WalkthroughConfig | null;
  onClose: () => void;
}

export const Walkthrough: React.FC<WalkthroughProps> = ({ config, onClose }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isWaitingForClick, setIsWaitingForClick] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const currentStep = config?.steps[currentStepIndex];

  // Find and highlight target element
  useEffect(() => {
    if (!config || !currentStep) {
      setTargetElement(null);
      setTargetRect(null);
      return;
    }

    const findTarget = async () => {
      // Run onBeforeStep if provided
      if (currentStep.onBeforeStep) {
        await currentStep.onBeforeStep();
      }

      // Wait a bit for DOM to update after onBeforeStep
      await new Promise(resolve => setTimeout(resolve, 300));

      // Try to find the target element
      const element = document.querySelector(currentStep.targetSelector) as HTMLElement;
      
      if (!element) {
        if (currentStep.skipIfNotFound) {
          // Skip to next step
          if (currentStepIndex < config.steps.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
          } else {
            onClose();
          }
          return;
        }
        // Retry finding element periodically
        checkIntervalRef.current = setInterval(() => {
          const retryElement = document.querySelector(currentStep.targetSelector) as HTMLElement;
          if (retryElement) {
            setTargetElement(retryElement);
            updateTargetRect(retryElement);
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
          }
        }, 500);
        return;
      }

      setTargetElement(element);
      updateTargetRect(element);

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

      // Wait for scroll to complete
      setTimeout(() => {
        updateTargetRect(element);
      }, 500);

      // If waitForClick is true, set up click listener
      if (currentStep.waitForClick) {
        setIsWaitingForClick(true);
        const handleClick = (e: MouseEvent) => {
          const clickedElement = e.target as HTMLElement;
          // Check if clicked element is the target or a child of target
          if (element.contains(clickedElement) || element === clickedElement) {
            e.stopPropagation();
            e.preventDefault();
            
            // Remove listener
            element.removeEventListener('click', handleClick);
            setIsWaitingForClick(false);

            // Run onAfterStep if provided
            if (currentStep.onAfterStep) {
              Promise.resolve(currentStep.onAfterStep()).then(() => {
                proceedToNextStep();
              });
            } else {
              proceedToNextStep();
            }
          }
        };

        // Add click listener with capture to intercept
        element.addEventListener('click', handleClick, true);
        
        return () => {
          element.removeEventListener('click', handleClick, true);
        };
      }
    };

    findTarget();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [config, currentStepIndex, currentStep]);

  const updateTargetRect = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setTargetRect(rect);
  };

  // Update rect on scroll/resize
  useEffect(() => {
    if (!targetElement) return;

    const updateRect = () => {
      updateTargetRect(targetElement);
    };

    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetElement]);

  const proceedToNextStep = () => {
    if (!config) return;

    if (currentStepIndex < config.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Walkthrough complete
      if (config.onComplete) {
        config.onComplete();
      }
      onClose();
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setIsWaitingForClick(false);
    }
  };

  const handleSkip = () => {
    if (config?.onSkip) {
      config.onSkip();
    }
    onClose();
  };

  if (!config || !currentStep) {
    return null;
  }

  // Calculate tooltip position
  const getTooltipPosition = () => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const position = currentStep.position || 'bottom';
    const spacing = 20;
    const tooltipWidth = 320;
    const tooltipHeight = 150;

    switch (position) {
      case 'top':
        return {
          top: `${targetRect.top - tooltipHeight - spacing}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          top: `${targetRect.bottom + spacing}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.left - tooltipWidth - spacing}px`,
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.right + spacing}px`,
          transform: 'translateY(-50%)',
        };
      case 'center':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translate(-50%, -50%)',
        };
      default:
        return {
          top: `${targetRect.bottom + spacing}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)',
        };
    }
  };

  // Calculate highlight overlay (darken everything except target)
  const getHighlightStyle = () => {
    if (!targetRect) return {};

    return {
      clipPath: `polygon(
        0% 0%, 
        0% 100%, 
        ${targetRect.left}px 100%, 
        ${targetRect.left}px ${targetRect.top}px, 
        ${targetRect.right}px ${targetRect.top}px, 
        ${targetRect.right}px ${targetRect.bottom}px, 
        ${targetRect.left}px ${targetRect.bottom}px, 
        ${targetRect.left}px 100%, 
        100% 100%, 
        100% 0%
      )`,
    };
  };

  const tooltipPosition = getTooltipPosition();

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[10000] pointer-events-auto"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        ...getHighlightStyle(),
      }}
      onClick={(e) => {
        // Prevent clicks outside target when waiting for click
        if (isWaitingForClick) {
          e.stopPropagation();
        }
      }}
    >
      {/* Highlight border around target */}
      {targetRect && (
        <div
          className="absolute border-4 border-accent-500 rounded-lg shadow-2xl pointer-events-none"
          style={{
            top: `${targetRect.top - 4}px`,
            left: `${targetRect.left - 4}px`,
            width: `${targetRect.width + 8}px`,
            height: `${targetRect.height + 8}px`,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 20px rgba(59, 130, 246, 0.5)',
          }}
        />
      )}

      {/* Tooltip */}
      {targetRect && (
        <div
          ref={tooltipRef}
          className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 max-w-sm pointer-events-auto"
          style={tooltipPosition}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-accent-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {currentStep.title}
              </h3>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
            {currentStep.description}
          </p>

          {/* Waiting indicator */}
          {isWaitingForClick && (
            <div className="mb-4 p-3 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 rounded-lg border border-accent-200 dark:border-accent-800">
              <p className="text-sm text-accent-700 dark:text-accent-300 font-medium">
                👆 Click the highlighted element to continue
              </p>
            </div>
          )}

          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Step {currentStepIndex + 1} of {config.steps.length}</span>
              <span>{Math.round(((currentStepIndex + 1) / config.steps.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-accent-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / config.steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousStep}
              disabled={currentStepIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            {!isWaitingForClick && (
              <button
                onClick={proceedToNextStep}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-500 rounded-lg hover:bg-accent-600"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {isWaitingForClick && (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                Waiting for click...
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

